import { ComponentProps, useMemo } from 'react';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { Button } from 'app/agriculture/components/Button/Button';
import { LinkIcon } from 'app/agriculture/icons';
import { routeConfig } from 'app/agriculture/router/routeConfig';
import { Entity } from 'app/agriculture/queries/entities/types/Entity';
import { useDevicesByType } from 'app/agriculture/queries/devices/useDevicesByType';
import { EntityType } from 'app/agriculture/models/Entities';
import { useFarmPath } from 'app/agriculture/router/useFarmPath';
import { useNavigate } from 'react-router-dom';
import { Device } from 'app/agriculture/queries/devices/types/Device';

interface InfrastructureActionsProps {
    id: string | number;
    type: EntityType;
    sticky?: boolean;
}

export function InfrastructureActions({ id, type, sticky = false }: InfrastructureActionsProps) {
    const navigate = useNavigate();
    const routeActions = useRouteActions();

    const typeSpecificActions = useTypeSpecificActions({ id, type });

    return (
        <Actions
            sticky={sticky}
            primaryAction={{
                text: 'Edit',
                startIcon: 'edit',
                onClick: () => {
                    navigate('edit');
                },
            }}
            secondaryActions={[
                {
                    text: 'Add Note',
                    startIcon: 'note',
                    onClick: () => {
                        routeActions.create('Note', { parentId: id });
                    },
                },
                {
                    text: 'Add Task',
                    startIcon: 'task',
                    onClick: () => {
                        routeActions.create('Task', { parentId: id });
                    },
                },
                ...typeSpecificActions,
                {
                    text: 'Delete',
                    startIcon: 'delete',
                    theme: 'danger',
                    onClick: () => {
                        routeActions.remove(id, { type });
                    },
                },
            ]}
        />
    );
}

// This is where all the special cased entity type specific actions should live
function useTypeSpecificActions(entity: Entity): ComponentProps<typeof Button>[] {
    const navigate = useNavigate();
    const farmPath = useFarmPath();
    const tankSensors = useDevicesByType('Level Monitor');
    const routeActions = useRouteActions();

    const tankSensorsByInfrastructureId = useMemo(() => {
        const map = new Map<string | number, Device>();
        for (const [, tankSensor] of tankSensors) {
            const { infrastructureId } = tankSensor;
            if (infrastructureId != null) {
                map.set(infrastructureId, tankSensor);
            }
        }
        return map;
    }, [tankSensors]);

    switch (entity.type) {
        case 'Tank': {
            return [
                tankSensorsByInfrastructureId.get(entity.id) == null
                    ? {
                          startIcon: <LinkIcon />,
                          text: 'Link Device',
                          onClick: () => {
                              navigate(
                                  farmPath(
                                      `/${routeConfig.Tank.rootPath}/${entity.id.toString()}/link`,
                                  ),
                              );
                          },
                      }
                    : {
                          startIcon: <LinkIcon />,
                          text: 'Unlink Device',
                          onClick: () => {
                              navigate(
                                  farmPath(
                                      `/${routeConfig.Tank.rootPath}/${entity.id.toString()}/unlink`,
                                  ),
                              );
                          },
                      },
            ];
        }
        case 'Paddock': {
            return [
                {
                    text: 'Add Herd',
                    startIcon: 'herd',
                    onClick: () => {
                        routeActions.create('Herd', { parentId: entity.id });
                    },
                },
            ];
        }
        default:
            return [];
    }
}
